import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Button,
  useTheme,
  Grid,
  CircularProgress,
  Avatar,
  Fade,
  FormControl,
  InputLabel,
  Select,
  Alert,
} from '@mui/material';
import {
  TrendingDown as TrendingDownIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  MoreVert as MoreVertIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
  Layers as LayersIcon,
  EmailOutlined as EmailOutlinedIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import apiService from '../services/apiService';
import { fetchAllCollectionsFromApi, setCachedJobsList } from '../utils/jobsListCache';
import { LEAD_SOURCE_OPTIONS, labelForLeadSource } from '../constants/leadSources';
import {
  countNewOnlineBookings,
  isNewOnlineBooking,
  JOBS_FILTER_NEW_ONLINE,
  NEW_ONLINE_BOOKING_LABEL,
} from '../utils/onlineBooking';

dayjs.extend(isoWeek);

/** Select value for “all lead sources”; must not be '' — MUI outlined label overlaps displayEmpty text. */
const ALL_LEAD_SOURCES = '__all__';

const JOB_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PERIOD_OPTIONS = [
  { id: 'today', label: 'Today' },
  { id: '2d', label: 'Last 2 days' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'year', label: 'This year' },
  { id: 'all', label: 'All time' },
];

/** @returns {{ start: import('dayjs').Dayjs, end: import('dayjs').Dayjs } | null} */
function getPeriodRange(periodId) {
  if (periodId === 'all') return null;
  const now = dayjs();
  switch (periodId) {
    case 'today':
      return { start: now.startOf('day'), end: now.endOf('day') };
    case '2d':
      return { start: now.subtract(1, 'day').startOf('day'), end: now.endOf('day') };
    case 'week':
      return { start: now.startOf('isoWeek'), end: now.endOf('isoWeek') };
    case 'month':
      return { start: now.startOf('month'), end: now.endOf('month') };
    case 'year':
      return { start: now.startOf('year'), end: now.endOf('year') };
    default:
      return null;
  }
}

function calendarDayInRange(dateStr, range) {
  if (!range) return true;
  if (!dateStr) return false;
  const d = dayjs(String(dateStr).slice(0, 10)).startOf('day');
  const { start, end } = range;
  return !d.isBefore(start.startOf('day')) && !d.isAfter(end.startOf('day'));
}

function dateTimeInRange(iso, range) {
  if (!range) return true;
  if (!iso) return false;
  const t = dayjs(iso);
  return t.valueOf() >= range.start.valueOf() && t.valueOf() <= range.end.valueOf();
}

function jobInPeriod(job, range) {
  if (!range) return true;
  // Include jobs created in the period even if scheduled for a different day.
  // Also include jobs scheduled in the period (even if created earlier).
  const createdMatch = dateTimeInRange(job.created_at, range);
  const scheduledMatch = job.scheduled_date
    ? calendarDayInRange(String(job.scheduled_date).slice(0, 10), range)
    : false;
  return createdMatch || scheduledMatch;
}

function clientInPeriod(client, range) {
  if (!range) return true;
  return dateTimeInRange(client.created_at, range);
}

function invoiceInPeriod(inv, range) {
  if (!range) return true;
  if (!inv.invoice_date) return false;
  return calendarDayInRange(String(inv.invoice_date).slice(0, 10), range);
}

function expenseInPeriod(exp, range) {
  if (!range) return true;
  if (!exp.date) return false;
  return calendarDayInRange(String(exp.date).slice(0, 10), range);
}

function periodCaption(periodId) {
  switch (periodId) {
    case 'today':
      return "Figures for today.";
    case '2d':
      return 'Figures for the last two calendar days (including today).';
    case 'week':
      return 'Figures for this calendar week (Mon–Sun).';
    case 'month':
      return 'Figures for this calendar month.';
    case 'year':
      return 'Figures for this calendar year.';
    case 'all':
    default:
      return 'All-time totals across your data.';
  }
}

/** Clients list is paginated (default 10/page, max 100) — load all pages for dashboard. */
async function fetchAllClients(apiService) {
  const perPage = 100;
  let page = 1;
  let lastPage = 1;
  const out = [];
  do {
    const res = await apiService.get(`clients?per_page=${perPage}&page=${page}`);
    if (Array.isArray(res)) {
      return res;
    }
    const chunk = Array.isArray(res?.data) ? res.data : [];
    out.push(...chunk);
    lastPage = Math.max(1, Number(res?.meta?.last_page ?? res?.last_page) || 1);
    page += 1;
  } while (page <= lastPage);
  return out;
}

/** Unique clients for a period: newly created then, or with at least one job in that window. */
function countClientsForPeriod(rawClients, jobsInPeriod, range) {
  if (!range) {
    return rawClients.length;
  }
  const ids = new Set();
  rawClients.forEach((c) => {
    if (clientInPeriod(c, range)) {
      ids.add(c.id);
    }
  });
  jobsInPeriod.forEach((j) => {
    if (j.client_id) {
      ids.add(j.client_id);
    }
  });
  return ids.size;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();

  const [rawJobs, setRawJobs] = useState([]);
  const [rawClients, setRawClients] = useState([]);
  const [rawInvoices, setRawInvoices] = useState([]);
  const [rawExpenses, setRawExpenses] = useState([]);
  const [period, setPeriod] = useState('today');
  const [leadSourceFilter, setLeadSourceFilter] = useState(ALL_LEAD_SOURCES);
  const [jobStatusFilter, setJobStatusFilter] = useState('');

  const range = useMemo(() => getPeriodRange(period), [period]);

  const newOnlineBookingCount = useMemo(() => countNewOnlineBookings(rawJobs), [rawJobs]);

  const { stats, recentJobs } = useMemo(() => {
    let jobs = rawJobs.filter((j) => jobInPeriod(j, range));
    if (leadSourceFilter !== ALL_LEAD_SOURCES) {
      jobs = jobs.filter((j) => (j.lead_source || '') === leadSourceFilter);
    }
    if (jobStatusFilter) {
      jobs = jobs.filter(
        (j) => String(j.status || 'pending').toLowerCase() === jobStatusFilter
      );
    }
    const invoices = rawInvoices.filter((i) => invoiceInPeriod(i, range));
    const expenses = rawExpenses.filter((e) => expenseInPeriod(e, range));

    const completedJobs = jobs.filter((job) => String(job.status || '').toLowerCase() === 'completed').length;
    const activeJobs = jobs.filter((job) => String(job.status || '').toLowerCase() !== 'completed').length;
    const totalClients = countClientsForPeriod(rawClients, jobs, range);
    const totalRevenue = invoices.reduce((sum, inv) => {
      const n = Number(inv.total ?? inv.amount ?? inv.subtotal);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
    const totalExpenses = expenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);

    const sortedRecent = [...jobs]
      .sort((a, b) => {
        const da = dayjs(a.scheduled_date || a.created_at);
        const db = dayjs(b.scheduled_date || b.created_at);
        return db.valueOf() - da.valueOf();
      })
      .slice(0, 6);

    return {
      stats: {
        totalJobs: jobs.length,
        completedJobs,
        activeJobs,
        totalClients,
        totalRevenue,
        totalExpenses,
      },
      recentJobs: sortedRecent,
    };
  }, [rawJobs, rawClients, rawInvoices, rawExpenses, range, leadSourceFilter, jobStatusFilter]);

  const [loading, setLoading] = useState(true);
  const [estimateSummary, setEstimateSummary] = useState(null);
  const [leadSummary, setLeadSummary] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  // Mock checking user name for greeting
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const greeting = `Welcome back, ${user.name ? user.name.split(' ')[0] : 'Admin'} 👋`;

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const [jobs, clients, invoicesData, expensesData] = await Promise.all([
          fetchAllCollectionsFromApi(),
          fetchAllClients(apiService),
          apiService.get('/invoices'),
          apiService.get('/expenses'),
        ]);

        const invoices = Array.isArray(invoicesData) ? invoicesData : (invoicesData.data || []);
        const expenses = Array.isArray(expensesData) ? expensesData : (expensesData.data || []);

        setRawJobs(jobs);
        setCachedJobsList(jobs);
        setRawClients(clients);
        setRawInvoices(invoices);
        setRawExpenses(expenses);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (error.message === 'Session expired') return;
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = getPeriodRange(period);
        const qs =
          r == null
            ? ''
            : `?created_from=${encodeURIComponent(r.start.format('YYYY-MM-DD'))}&created_to=${encodeURIComponent(r.end.format('YYYY-MM-DD'))}`;
        const data = await apiService.get(`estimates/summary${qs}`);
        if (!cancelled) {
          setEstimateSummary(data);
        }
      } catch {
        if (!cancelled) {
          setEstimateSummary(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [period]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = getPeriodRange(period);
        const qs =
          period === 'all'
            ? ''
            : `?created_from=${encodeURIComponent(r.start.format('YYYY-MM-DD'))}&created_to=${encodeURIComponent(r.end.format('YYYY-MM-DD'))}`;
        const data = await apiService.get(`leads/summary${qs}`);
        if (!cancelled) {
          setLeadSummary(data);
        }
      } catch {
        if (!cancelled) {
          setLeadSummary(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [period]);

  const handleMenuOpen = (event, job) => {
    setMenuAnchor(event.currentTarget);
    setSelectedJob(job);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedJob(null);
  };

  const handleViewJob = () => {
    if (selectedJob) {
      navigate('/jobs', { state: { action: 'view', jobId: selectedJob.id } });
      handleMenuClose();
    }
  };

  const handleEditJob = () => {
    if (selectedJob) {
      navigate('/jobs', { state: { action: 'edit', jobId: selectedJob.id } });
      handleMenuClose();
    }
  };

  const handleDeleteJob = async () => {
    if (selectedJob) {
      if (window.confirm('Are you sure you want to delete this job?')) {
        try {
          await apiService.delete(`/collections/${selectedJob.id}`);
          window.location.reload();
        } catch (error) {
          console.error('Error deleting job:', error);
        }
      }
    }
    handleMenuClose();
  };

  const renderStatCard = ({ title, value, icon, color, onClick }) => (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { boxShadow: theme.palette.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.35)' : '0 2px 8px rgba(15,23,42,0.08)' } : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1.5 }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', zIndex: 1, flex: 1, wordBreak: 'break-word', lineHeight: 1.35, fontSize: '0.6875rem' }}>
            {title}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color || theme.palette.primary.main,
              zIndex: 1,
              '& svg': { fontSize: 22 },
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, zIndex: 1, fontSize: '1.35rem' }}>
          {typeof value === 'number' && (title.toLowerCase().includes('revenue') || title.toLowerCase().includes('expense'))
            ? `£${value.toLocaleString()}`
            : typeof value === 'number'
            ? value.toLocaleString()
            : value}
        </Typography>
      </CardContent>
    </Card>
  );

  const renderJobCard = (job) => {
    const st = String(job.status || 'pending').toLowerCase();
    const isCompleted = st === 'completed';
    const isPending = st === 'pending';
    const leadLabel = labelForLeadSource(job.lead_source);
    
    return (
      <Box
        sx={{
          p: 1.5,
          mb: 1.25,
          borderRadius: '8px',
          background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#ffffff',
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          '&:hover': {
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(79,70,229,0.04)',
            borderColor: theme.palette.primary.main + '33'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0, mr: 1.5 }}>
          <Avatar
            variant="rounded"
            sx={{
              width: 40,
              height: 40,
              minWidth: 40,
              backgroundColor: isCompleted ? theme.palette.success.main + '20' : theme.palette.primary.main + '20',
              color: isCompleted ? theme.palette.success.main : theme.palette.primary.main,
              fontWeight: 700
            }}
          >
            {(job.client_name || job.client || 'U').charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, mb: 0.25, lineHeight: 1.25, fontSize: '0.875rem' }}>
              {job.client_name || job.client || 'Unknown Client'}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
              {job.description || 'No description'} • {new Date(job.created_at || job.date).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {isNewOnlineBooking(job) ? (
            <Chip
              label={NEW_ONLINE_BOOKING_LABEL}
              size="small"
              color="secondary"
              sx={{ fontWeight: 800, fontSize: '0.65rem', height: 22 }}
            />
          ) : null}
          {leadLabel ? (
            <Chip
              label={leadLabel}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.65rem', height: 22, maxWidth: 140 }}
            />
          ) : null}
          <Chip
            label={job.status?.replace('_', ' ') || 'pending'}
            size="small"
            sx={{
              backgroundColor: isCompleted ? theme.palette.success.main + '20' : isPending ? theme.palette.primary.main + '20' : theme.palette.warning.main + '20',
              color: isCompleted ? theme.palette.success.main : isPending ? theme.palette.primary.main : theme.palette.warning.main,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.7rem',
              height: 24,
              borderRadius: '8px'
            }}
          />
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, job)} sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ mt: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
        <CircularProgress size={48} thickness={4} sx={{ color: theme.palette.primary.main, mb: 3 }} />
        <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
          Loading your workspace...
        </Typography>
      </Box>
    );
  }

  return (
    <Fade in={!loading} timeout={600}>
      <Box sx={{ width: '100%', maxWidth: '1280px', margin: '0 auto', px: { xs: 0, md: 0 } }}>
        {/* Welcome Header */}
        <Box
          sx={{
            mb: 2.5,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            gap: 1.5,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" sx={{ color: theme.palette.text.primary, mb: 0.5, fontWeight: 700 }}>
              {greeting}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.25 }}>
              {periodCaption(period)}
            </Typography>
            {period !== 'all' && (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, display: 'block', opacity: 0.92, maxWidth: 560, lineHeight: 1.45 }}
              >
                Revenue is the sum of invoice totals whose invoice date falls in this period. Clients include new clients
                added in this period and existing clients with at least one job scheduled (or created) in this period.
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1,
              flexShrink: 0,
            }}
          >
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
              <InputLabel id="dashboard-period-label">Period</InputLabel>
              <Select
                labelId="dashboard-period-label"
                label="Period"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              >
                {PERIOD_OPTIONS.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
              <InputLabel id="dashboard-lead-label">Lead source</InputLabel>
              <Select
                labelId="dashboard-lead-label"
                label="Lead source"
                value={leadSourceFilter}
                onChange={(e) => setLeadSourceFilter(e.target.value)}
              >
                <MenuItem value={ALL_LEAD_SOURCES}>All sources</MenuItem>
                {LEAD_SOURCE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
              <InputLabel id="dashboard-status-label">Job status</InputLabel>
              <Select
                labelId="dashboard-status-label"
                label="Job status"
                value={jobStatusFilter}
                onChange={(e) => setJobStatusFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
              >
                {JOB_STATUS_FILTER_OPTIONS.map((o) => (
                  <MenuItem key={o.value || 'all'} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={() => navigate('/jobs', { state: { action: 'create' } })}
              sx={{ py: 0.75, px: 2, boxShadow: 'none' }}
            >
              Create New Job
            </Button>
          </Box>
        </Box>

        {newOnlineBookingCount > 0 ? (
          <Alert
            severity="info"
            sx={{ mb: 2, alignItems: 'center' }}
            action={
              <Button
                color="inherit"
                size="small"
                sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                onClick={() =>
                  navigate('/jobs', {
                    state: { [JOBS_FILTER_NEW_ONLINE]: true },
                  })
                }
              >
                View jobs
              </Button>
            }
          >
            <strong>
              {newOnlineBookingCount} new online booking{newOnlineBookingCount !== 1 ? 's' : ''}
            </strong>{' '}
            awaiting review (pending, from the public booking form).
          </Alert>
        ) : null}

        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard({
              title: "Total Jobs",
              value: stats.totalJobs,
              icon: <AssignmentIcon />,
              color: theme.palette.primary.main,
              onClick: () => navigate('/jobs')
            })}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard({
              title: "Active Jobs",
              value: stats.activeJobs,
              icon: <ScheduleIcon />,
              color: theme.palette.warning.main,
              onClick: () => navigate('/jobs')
            })}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard({
              title: "Total Clients",
              value: stats.totalClients,
              icon: <PeopleIcon />,
              color: theme.palette.success.main,
              onClick: () => navigate('/clients')
            })}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderStatCard({
              title: "Revenue",
              value: stats.totalRevenue,
              icon: <ReceiptIcon />,
              color: theme.palette.secondary.main,
              onClick: () => navigate('/invoices')
            })}
          </Grid>
        </Grid>

        {leadSummary && (
          <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                  mb: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <PeopleIcon color="secondary" />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Leads funnel
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Pipeline stages (created in the selected period{period === 'all' ? '; all time' : ''}). Open: {leadSummary.open ?? 0}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/leads')}
                  sx={{ textTransform: 'none', flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
                >
                  Open leads
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {[
                  { key: 'new', label: 'New' },
                  { key: 'contacted', label: 'Contacted' },
                  { key: 'qualified', label: 'Qualified' },
                  { key: 'quoted', label: 'Quoted' },
                  { key: 'won', label: 'Won', color: 'success' },
                  { key: 'lost', label: 'Lost', color: 'default' },
                ].map(({ key, label, color }) => (
                  <Chip
                    key={key}
                    size="small"
                    color={color || 'primary'}
                    variant={color === 'default' ? 'outlined' : 'filled'}
                    label={`${label}: ${leadSummary.by_status?.[key] ?? 0}`}
                    sx={{ fontWeight: 700 }}
                  />
                ))}
                <Chip size="small" variant="outlined" label={`Total: ${leadSummary.total ?? 0}`} sx={{ fontWeight: 700 }} />
              </Box>
              {(leadSummary.follow_up?.overdue > 0 || leadSummary.follow_up?.due_today > 0) ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                  {leadSummary.follow_up?.overdue > 0 ? (
                    <Chip
                      size="small"
                      color="error"
                      label={`Overdue follow-ups: ${leadSummary.follow_up.overdue}`}
                      clickable
                      onClick={() => navigate('/leads?follow_up=overdue')}
                    />
                  ) : null}
                  {leadSummary.follow_up?.due_today > 0 ? (
                    <Chip
                      size="small"
                      color="warning"
                      label={`Due today: ${leadSummary.follow_up.due_today}`}
                      clickable
                      onClick={() => navigate('/leads?follow_up=due_today')}
                    />
                  ) : null}
                </Box>
              ) : null}
            </CardContent>
          </Card>
        )}

        {estimateSummary && (
          <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                  mb: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <DescriptionIcon color="primary" />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Estimates pipeline
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Counts by status (created in the selected period{period === 'all' ? '; all time' : ''}).
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/estimates')}
                  sx={{ textTransform: 'none', flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
                >
                  Open estimates
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {[
                  { key: 'draft', label: 'Draft', color: 'default' },
                  { key: 'sent', label: 'Sent', color: 'primary' },
                  { key: 'accepted', label: 'Accepted', color: 'success' },
                  { key: 'rejected', label: 'Rejected', color: 'error' },
                ].map(({ key, label, color }) => (
                  <Chip
                    key={key}
                    size="small"
                    color={color === 'default' ? 'default' : color}
                    variant={color === 'default' ? 'outlined' : 'filled'}
                    label={`${label}: ${estimateSummary.by_status?.[key] ?? 0}`}
                    sx={{ fontWeight: 700 }}
                  />
                ))}
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Total: ${estimateSummary.total ?? 0}`}
                  sx={{ fontWeight: 700 }}
                />
              </Box>
            </CardContent>
          </Card>
        )}

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            alignItems: 'start',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {/* Recent Activity List */}
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                Recent activity {period !== 'all' ? '(this period)' : ''}
              </Typography>
              <Button size="small" onClick={() => navigate('/jobs')} sx={{ fontWeight: 600 }}>
                View All
              </Button>
            </Box>

            <Box>
              {recentJobs.length > 0 ? (
                recentJobs.map((job) => <React.Fragment key={job.id}>{renderJobCard(job)}</React.Fragment>)
              ) : (
                <Card sx={{ p: 4, textAlign: 'center', background: 'transparent', border: '1px dashed ' + theme.palette.divider, boxShadow: 'none' }}>
                  <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary, mb: 0.5, fontWeight: 600 }}>No recent activity</Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {leadSourceFilter !== ALL_LEAD_SOURCES || jobStatusFilter
                      ? 'No jobs match the selected lead source or status filters. Clear filters or widen the period.'
                      : period === 'all'
                      ? 'Jobs and collections will appear here once created.'
                      : 'No jobs scheduled or created in this period. Try a wider range or All time.'}
                  </Typography>
                </Card>
              )}
            </Box>
          </Box>

          {/* Quick Actions: до Recent activity на md+, мрежа 4×2 */}
          <Card sx={{ width: '100%', height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: '1rem' }}>
                Quick Actions
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                }}
              >
                {[
                  { title: 'New Job', icon: <AssignmentIcon />, color: '#4F46E5', bg: 'rgba(79, 70, 229, 0.1)', to: '/jobs', state: { action: 'create' } },
                  { title: 'New Client', icon: <PeopleIcon />, color: '#10B981', bg: 'rgba(16, 185, 129, 0.1)', to: '/clients', state: { action: 'create' } },
                  { title: 'New Invoice', icon: <ReceiptIcon />, color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)', to: '/invoices', state: { action: 'create' } },
                  { title: 'New Expense', icon: <TrendingDownIcon />, color: '#EF4444', bg: 'rgba(239, 68, 68, 0.1)', to: '/expenses', state: { action: 'create' } },
                  { title: 'Estimates', icon: <DescriptionIcon />, color: '#6366F1', bg: 'rgba(99, 102, 241, 0.1)', to: '/estimates' },
                  { title: 'Price Book', icon: <MenuBookIcon />, color: '#0EA5E9', bg: 'rgba(14, 165, 233, 0.1)', to: '/price-book' },
                  { title: 'Estimate templates', icon: <LayersIcon />, color: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.1)', to: '/estimate-templates' },
                  { title: 'Message templates', icon: <EmailOutlinedIcon />, color: '#64748B', bg: 'rgba(100, 116, 139, 0.1)', to: '/message-templates' },
                ].map((action, idx) => (
                  <Button
                    key={idx}
                    fullWidth
                    onClick={() => navigate(action.to, action.state ? { state: action.state } : {})}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      p: { xs: 1.25, sm: 1.5 },
                      height: '100%',
                      minHeight: 88,
                      borderRadius: '8px',
                      background: action.bg,
                      color: action.color,
                      border: `1px solid transparent`,
                      transition: 'border-color 0.15s ease, background-color 0.15s ease',
                      '&:hover': {
                        background: action.bg,
                        border: `1px solid ${action.color}55`,
                        boxShadow: 'none',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '6px',
                        background: `${action.color}22`,
                        flexShrink: 0,
                      }}
                    >
                      {React.cloneElement(action.icon, { fontSize: 'small' })}
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', textAlign: 'center' }}>
                      {action.title}
                    </Typography>
                  </Button>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Job Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 8,
            sx: {
              borderRadius: '12px',
              minWidth: 160,
              mt: 1,
              border: `1px solid ${theme.palette.divider}`,
              backgroundImage: 'none'
            }
          }}
        >
          <MenuItem onClick={handleViewJob} sx={{ py: 1.5, mx: 1, borderRadius: '8px', mb: 0.5 }}>
            <VisibilityIcon sx={{ mr: 1.5, fontSize: 20, color: theme.palette.text.secondary }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>View Details</Typography>
          </MenuItem>
          <MenuItem onClick={handleEditJob} sx={{ py: 1.5, mx: 1, borderRadius: '8px', mb: 0.5 }}>
            <EditIcon sx={{ mr: 1.5, fontSize: 20, color: theme.palette.text.secondary }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Edit Job</Typography>
          </MenuItem>
          <MenuItem onClick={handleDeleteJob} sx={{ py: 1.5, mx: 1, borderRadius: '8px', color: theme.palette.error.main, '&:hover': { background: theme.palette.error.main + '20' } }}>
            <DeleteIcon sx={{ mr: 1.5, fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Delete Job</Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Fade>
  );
}